import { db } from "@/repositories/database";
import type {
  DRLocalDrawingData,
  DRLocalDrawingMeta,
  DRRevisionBinaryKey,
  DRRevisionData,
  DRDrawing,
  DRUser,
  OdaBinaryFormat,
} from "@/types";
import type { RepositoryBaseInterface } from "@/repositories/repository.interface";
import { map } from "lodash";
import { BaseRepository } from "@/repositories/base.repository";
import { drawingRepository } from "./drawing.repository";
import { backupRepository } from "./backup.repository";
import { activityEventRepository } from "./activityEvent.repository";

const SAVES_COUNT_THRESHOLD = 5;

/**
 * Projette un dessin local sur ses métadonnées : aucun `ArrayBuffer` n'est référencé
 * par l'objet retourné, seulement deux booléens de présence de brouillon.
 */
function toLocalDrawingMeta(drawing: DRLocalDrawingData): DRLocalDrawingMeta {
  const { lastRevision } = drawing;
  // Copie puis suppression (plutôt qu'un rest-destructuring) pour rester robuste à
  // l'ajout de champs sur DRRevision : tout nouveau champ non binaire est conservé.
  const revision: Partial<DRRevisionData> = { ...lastRevision };
  delete revision.vsf;
  delete revision.dwg;
  delete revision.draft;
  delete revision.dwgDraft;

  return {
    ...drawing,
    lastRevision: {
      // Vrai par construction : les 4 clés binaires viennent d'être supprimées.
      ...(revision as Omit<DRRevisionData, DRRevisionBinaryKey>),
      hasDraft: !!lastRevision.draft,
      hasDwgDraft: !!lastRevision.dwgDraft,
    },
  };
}

class LocalDrawingRepository
  extends BaseRepository<DRLocalDrawingData, number>
  implements RepositoryBaseInterface<DRLocalDrawingData>
{
  constructor() {
    super(db.drawings, "drawing");
  }

  /**
   * ATTENTION : renvoie les dessins AVEC leurs binaires (`dwg`/`dwgDraft`/...), soit
   * plusieurs Mo par dessin. Ne l'utiliser que si le contenu des buffers est
   * réellement nécessaire, et jamais pour alimenter un état durable — préférer
   * `fetchAllMetaByNewest()` puis `getByIdOrThrow(id)` sur le seul dessin utile.
   */
  fetchAllByNewest() {
    const drawings = db.drawings.reverse().sortBy("createdAt");
    return drawings;
  }

  /**
   * Idem, mais projeté sur les métadonnées (cf. `DRLocalDrawingMeta`) : les binaires
   * sont écartés dès la sortie de Dexie, donc collectables immédiatement au lieu
   * d'être retenus par l'appelant.
   *
   * Le pic transitoire de désérialisation subsiste — IndexedDB ne sait pas lire une
   * ligne partiellement. Seule la RÉTENTION est supprimée, et c'est elle qui coûte :
   * `fileStore` gardait ces buffers vivants en permanence, rechargés à chaque
   * écriture sur `db.drawings`. Supprimer aussi le pic demanderait de sortir les
   * binaires de la table (hors périmètre).
   */
  async fetchAllMetaByNewest(): Promise<DRLocalDrawingMeta[]> {
    const drawings = await this.fetchAllByNewest();
    return drawings.map(toLocalDrawingMeta);
  }

  /**
   * Ids des dessins locaux, lus depuis l'index primaire : aucune ligne n'est
   * désérialisée, donc aucun binaire ne transite. À préférer dès qu'on ne compare
   * que des ids (cf. `Syncer.removeUnusedImagesInDb`).
   */
  getAllIds(): Promise<number[]> {
    return db.drawings.toCollection().primaryKeys();
  }

  replace(drawing: DRLocalDrawingData) {
    return db.drawings.put(drawing);
  }

  async update(
    fileId: number,
    update: Partial<Omit<DRLocalDrawingData, "id">>
  ) {
    return await db.drawings.update(fileId, update);
  }

  async pruneManyFromList(files: DRLocalDrawingData[]): Promise<number> {
    const fileIds = map(files, "id");
    return this.pruneManyFromIds(fileIds, "id");
  }

  createMany(items: DRLocalDrawingData[]) {
    return db.transaction(
      "rw",
      db.drawings,
      async (): Promise<{ created: number[] }> => {
        const { failed, created } = await this.createMany2(items);
        if (failed.length) {
          // already existed, so we're updating them
          await Promise.all(
            failed.map(async (failedId) => {
              const failedItem = items.find((item) => item.id === failedId);
              if (!failedItem) {
                throw new Error(
                  "Item which insertion was rejected cannot be found in initial items array"
                );
              }

              const existing = await db.drawings.get(failedId);
              if (!existing) {
                throw new Error(
                  "Item which insertion was rejected cannot be found in db"
                );
              }
            })
          );
        }
        return { created };
      }
    );
  }

  /**
   * Save DWG file from S3 to local storage.
   * Clears any existing draft as the revision is now synced.
   *
   * Point d'entrée UNIQUE d'un DWG dans `db.drawings` (cf. `Syncer.makeDwgLocal`) :
   * c'est donc ici qu'est daté le passage « disponible hors ligne » via
   * `localizedAt`, sur lequel s'appuie la politique de rétention locale (cf.
   * `getLocalRetentionDate`). Gratuit : la ligne entière est réécrite de toute
   * façon à cet endroit.
   */
  async saveLastRevisionDwg(
    drawing: DRDrawing,
    dwg: ArrayBuffer
  ): Promise<void> {
    const revisionData = {
      ...drawing.lastRevision,
      dwg,
      draft: undefined, // Clear VSFX draft after sync
      dwgDraft: undefined, // Clear DWG draft after sync
    };
    const localizedAt = Date.now();

    const alreadyExist = await this.createSafeById({
      id: drawing.id,
      initialOpenCloudFileId: drawing.initialOpenCloudFileId,
      lastRevision: revisionData,
      localizedAt,
    });

    if (!alreadyExist) {
      await this.update(drawing.id, {
        lastRevision: revisionData,
        localizedAt,
      });
    }
  }

  async getLastSaveOrDraft(
    drawingId: number
  ): Promise<{ binary: ArrayBuffer; format: OdaBinaryFormat } | undefined> {
    const drawing = await this.getByIdOrThrow(drawingId);

    // Check for drafts first (unsaved changes)
    if (drawing.lastRevision.dwgDraft) {
      return {
        binary: drawing.lastRevision.dwgDraft,
        format: "DWG",
      };
    }
    if (drawing.lastRevision.draft) {
      return {
        binary: drawing.lastRevision.draft,
        format: "VSFX",
      };
    }

    // Then check for stored files
    if (drawing.lastRevision.dwg) {
      return {
        binary: drawing.lastRevision.dwg,
        format: "DWG",
      };
    }
    if (drawing.lastRevision.vsf) {
      return {
        binary: drawing.lastRevision.vsf,
        format: drawing.lastRevision.format,
      };
    }

    return undefined;
  }

  async resetSavesCount(drawingId: number): Promise<void> {
    await db.drawings
      .where("id")
      .equals(drawingId)
      .modify((drawing) => {
        drawing.savesCount = 0;
      });
  }

  /**
   * Save a DWG draft to local storage.
   * This is the primary save method for all new drawings.
   *
   * Brouillon ET compteur de sauvegardes dans UN SEUL `modify` : dans Dexie, toute
   * écriture sur `db.drawings` relit et réécrit l'enregistrement entier, binaires
   * compris (`Table.update` délègue à `Collection.modify`, qui fait `getMany` puis
   * réécrit). Incrémenter le compteur dans une seconde écriture coûtait donc un
   * cycle lecture/écriture complet de plusieurs Mo pour un entier — et rallongeait
   * `lastSaveDurationMs`, dont dépend `canSaveInBackground()` : une sauvegarde jugée
   * trop lente est purement abandonnée au passage en arrière-plan. Greffé sur
   * l'écriture du brouillon, l'incrément est gratuit.
   */
  async saveDraft(drawingId: number, draft: ArrayBuffer, autoSave = true) {
    let savesCount = 0;
    const res = await db.drawings
      .where("id")
      .equals(drawingId)
      .modify((drawing) => {
        drawing.lastRevision.dwgDraft = draft;
        drawing.lastRevision.editedAt = new Date();
        if (autoSave) {
          drawing.savesCount = (drawing.savesCount || 0) + 1;
          savesCount = drawing.savesCount;
        }
      });

    // `modify` renvoie le nombre de lignes touchées : 0 signifie que la ligne
    // locale du dessin n'existe plus (base vidée/évincée par le navigateur,
    // dessin retiré par la synchro pendant l'édition…). Sans cette garde, le
    // brouillon partait dans le vide et l'app affichait « sauvegardé ».
    if (res === 0) {
      throw new Error(
        `saveDraft: no local row for drawing ${drawingId} — draft NOT written`
      );
    }

    // Volontairement PAS attendu : le backup doit rester hors du temps de
    // sauvegarde mesuré (cf. ci-dessus). `createFromDrawingId` remet lui-même
    // `savesCount` à zéro une fois le backup créé.
    if (autoSave && savesCount >= SAVES_COUNT_THRESHOLD) {
      void backupRepository.createFromDrawingId(drawingId);
    }

    return res;
  }

  async addRasterPathOnLastRevision(
    drawingId: number,
    rasterPath: string
  ): Promise<void> {
    const drawing = await this.getByIdOrThrow(drawingId);
    if (!drawing.lastRevision.draftPaths) {
      drawing.lastRevision.draftPaths = [];
    }
    const paths = drawing.lastRevision.draftPaths ?? [];

    drawing.lastRevision.draftPaths = [...paths, rasterPath];
    await this.replace(drawing);
  }

  async deleteById(id: number): Promise<void> {
    await db.drawings.delete(id);
    // Libération IDB : on vide aussi l'oplog local de ce dessin.
    await activityEventRepository.clearByDrawing(id);
  }

  async deleteManyById(ids: number[]): Promise<void> {
    await db.drawings.bulkDelete(ids);
    await Promise.all(ids.map((id) => activityEventRepository.clearByDrawing(id)));
  }

  deleteAll() {
    return db.drawings.clear();
  }

  /**
   *
   * @param drawingid drawing id
   * @returns Original file id (the dwg that the drawing was converted from) on OCS
   */
  async getOriginalFileId(
    drawingid: number
  ): Promise<string | undefined | null> {
    const drawing = await this.getById(drawingid);
    return drawing?.initialOpenCloudFileId;
  }

  async setDrawingError(drawingId: number, error: string): Promise<void> {
    await db.drawings.update(drawingId, {
      error,
    });
  }

  async clearDrawingError(drawingId: number): Promise<void> {
    await db.drawings.update(drawingId, {
      error: undefined,
    });
  }

  async createTemporaryDrawing(params: {
    name: string;
    user: DRUser;
    projectId: number;
    organizationId: number;
    draft?: ArrayBuffer;
  }): Promise<DRLocalDrawingData> {
    const tempId = Date.now() * -1;
    const now = new Date();

    // No longer load empty.vsfx - the canvas will call Facade.file.createNew()
    const newDrawingData: DRLocalDrawingData = {
      id: tempId,
      lastRevision: {
        id: -1,
        vsf: undefined,
        draft: params.draft,
        editedAt: now,
        savedAt: now,
        format: "VSFX",
        drawingId: tempId,
        openCloudFileId: null,
      },
    };

    await db.drawings.add(newDrawingData);

    const file: DRDrawing = {
      id: newDrawingData.id,
      name: params.name,
      projectId: params.projectId,
      organizationId: params.organizationId,
      createdAt: now,
      createdBy: params.user,
      branchesCount: 1,
      lastRevision: {
        id: newDrawingData.lastRevision.id,
        savedAt: newDrawingData.lastRevision.savedAt,
        drawingId: newDrawingData.id,
        format: newDrawingData.lastRevision.format,
        openCloudFileId: newDrawingData.lastRevision.openCloudFileId,
      },
    };

    await drawingRepository.createSafeById(file);
    return newDrawingData;
  }

  async removeTemporaryDrawing(id: number): Promise<void> {
    if (id >= 0) {
      throw new Error("Cannot remove a permanent drawing with a temporary id");
    }
    await db.drawings.delete(id);
    await drawingRepository.deleteById(id);
    // Dessin temporaire abandonné : on vide ses events pending.
    await activityEventRepository.clearByDrawing(id);
  }
}

export const localDrawingRepository = new LocalDrawingRepository();
